// src/services/messaging/handlers/fileUploadedHandler.ts

import type { MessageHandlerResult } from "@/services/abstract/poc/MessagingService";
import type { HeliaNode } from "@/types/poc/helia";
import type { LibP2pWithServices } from "@/types/poc/libp2p";
import type { MessageHandler, MessagePayload } from "@/types/poc/message";
import { createLogger, type Logger } from "@/utils/logger";

const log: Logger = createLogger({ namespace: 'six3ro:p2p:handlers:file-ready-for-download' });

type FileReadyForDownloadResult = Extract<MessageHandlerResult, { type: 'file-ready-for-download' }>;

/**
 * Creates a pure file-ready-for-download message handler.
 * This handler processes file-ready-for-download messages without side effects.
 */
export const createFileUploadedHandler = (): MessageHandler<FileReadyForDownloadResult> => ({
    canHandle: (topic: string, message: MessagePayload): boolean =>
      message.type === 'file-ready-for-download',

    process: (
      message: MessagePayload,
      libp2p: LibP2pWithServices,
      heliaNode: HeliaNode | null
    ): FileReadyForDownloadResult => {
      if (message.type !== 'file-ready-for-download') {
        throw new Error(`Invalid message type: expected 'file-ready-for-download', got '${message.type}'`);
      }

      const { cid, metadata, sender } = message;
      log.info('Received file-ready-for-download for CID:', cid, 'from', sender);

      return {
        type: 'file-ready-for-download',
        data: {
          cid,
          metadata,
          sender,
          shouldReplicate: false,
          messageTimestamp: message.timestamp,
        },
      };
    }
});
